// filepath: apps/web/src/operator/OperatorTokenPrompt.tsx
//
// FR-UI-OPS-1 AC-2 — operator token input form.
//
// Renders a password-masked input + "Enter" submit button. On
// submit, the value is forwarded to `useOperatorToken().set()`
// which stores it in the in-memory store AND mirrors it to
// sessionStorage (ISS-223 partial INV-5 relaxation; see
// useOperatorToken.ts header for the relaxation scope). The
// prompt is rendered by `OperatorTokenGate` when no token is
// present, and re-rendered after a 401 clears the token.
//
// SECURITY INVARIANTS (INV-5, partial relaxation per ISS-223):
//   - The input is `type="password"`. The value is NEVER rendered
//     as text (no defaultValue echo, no on-screen display).
//   - The token IS written to sessionStorage on submit (relaxed
//     from the original INV-5 ban) under the namespaced key
//     `ascoa.operator.token`. It is cleared on clear() / 401.
//     localStorage / cookies / URL writes remain forbidden.
//   - The token is NEVER logged to console. (No `console.log`.)
//   - The optional `errorMessage` prop is a non-sensitive string
//     sourced from the i18n bundle — the server's error envelope
//     is NEVER echoed to the user (may contain internal paths).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Banner, Button, Card } from "@/design-system";
import { useOperatorToken } from "./useOperatorToken";

export interface OperatorTokenPromptProps {
  /**
   * Non-sensitive error message displayed under the input. The
   * caller resolves this from the i18n bundle — the server's
   * raw error envelope is NEVER echoed here (INV-5).
   */
  errorMessage?: string;
}

/**
 * OperatorTokenPrompt — controlled component. The value is held
 * in local React state so the input renders the masked value
 * during typing but never propagates to a parent. On submit, the
 * trimmed value is forwarded to the in-memory operator-token
 * store.
 */
export function OperatorTokenPrompt({
  errorMessage,
}: OperatorTokenPromptProps): React.ReactElement {
  const { t } = useTranslation();
  const { set } = useOperatorToken();
  const [value, setValue] = React.useState<string>("");

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const trimmed = value.trim();
    if (trimmed.length === 0) return;
    set(trimmed);
  };

  return (
    <main
      aria-labelledby="operator-token-prompt-title"
      data-testid="operator-token-prompt"
      className="operator-token-prompt"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--surface-page)",
        fontFamily: "var(--font-sans)",
        padding: "var(--space-8)",
      }}
    >
      <Card
        padding="lg"
        data-testid="operator-token-prompt-card"
        style={{ width: "420px", maxWidth: "90vw" }}
      >
        <h1
          id="operator-token-prompt-title"
          className="operator-token-prompt-title"
          style={{
            fontSize: "var(--text-2xl)",
            fontWeight: "var(--font-extrabold)",
            color: "var(--text-primary)",
            margin: "0 0 var(--space-4) 0",
          }}
        >
          {t("operator.tokenPrompt.label", "Operator token")}
        </h1>
        <form
          onSubmit={onSubmit}
          className="operator-token-prompt-form"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-3)",
          }}
        >
          <label
            htmlFor="operator-token-input"
            className="operator-token-prompt-label"
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--text-secondary)",
            }}
          >
            {t("operator.tokenPrompt.placeholder", "Paste your operator token")}
          </label>
          <input
            id="operator-token-input"
            // INV-5: type="password" so the value is masked on
            // screen, in shoulder-surfing visibility, and in
            // screen-recording captures. NEVER use type="text".
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            data-testid="operator-token-input"
            className="operator-token-input"
            style={{
              padding: "8px 12px",
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--border-strong)",
              fontFamily: "var(--font-mono, monospace)",
              fontSize: "var(--text-base)",
              background: "var(--surface-card)",
              color: "var(--text-primary)",
            }}
          />
          {errorMessage ? (
            <div
              data-testid="operator-token-error"
              className="operator-token-error"
            >
              <Banner tone="danger">{errorMessage}</Banner>
            </div>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={value.trim().length === 0}
            data-testid="operator-token-submit"
            className="operator-token-submit"
          >
            {t("operator.tokenPrompt.button", "Enter")}
          </Button>
        </form>
      </Card>
    </main>
  );
}
